import { useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import { useBenefitCalculationReads } from "@/features/benefit-workspace/useBenefitProgramReads"
import { IconCalculator, IconLoader2 } from "@tabler/icons-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { api } from "@/lib/api"
import { saveBenefitChange } from "@/lib/benefit-save"
import { visibleIncomeFormulaPrograms } from "@/lib/program-presentation"
import type { BenefitDetailDto } from "@/lib/benefit-types"

export function EligibilityCalculationSection({ benefit, canEdit, onUpdated }: {
  benefit: BenefitDetailDto; canEdit: boolean; onUpdated: (updated: BenefitDetailDto) => void
}) {
  const { options, readiness, loading, error: loadError, retry, canRetry } = useBenefitCalculationReads(benefit)
  const [saving, setSaving] = useBenefitSaving()
  const canCalculate = canEdit && !saving && !loading && !loadError && options.length > 0 && !!readiness &&
    readiness.programs.some(program => (program.requested || program.requiredByExistingGrant) && !program.dependencyOnly && program.readyForCalculation)

  const calculate = async () => {
    if (!canCalculate) return
    if (!benefit.concurrencyToken) { toast.error("Reload before calculating eligibility."); return }
    const programYear = options[0]?.programYear
    if (!programYear) { toast.error("The active program catalog could not be resolved."); return }
    setSaving(true)
    // The persisted request list is the only program selection. The calculator sends only its
    // version fence and catalog year; the server resolves requested and existing programs.
    await saveBenefitChange(benefit.id, onUpdated,
      () => api.post<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}/eligibility`, {
        concurrencyToken: benefit.concurrencyToken, programYear,
      }),
      "Eligibility calculated from the saved application.")
    setSaving(false)
  }

  return <Card>
    <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><IconCalculator className="size-5" />Calculate Eligibility</CardTitle>
      <CardDescription>Requested benefits and existing grants determine what can be calculated. Choose intent in Program Details.</CardDescription></CardHeader>
    <CardContent className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {benefit.benefitEligibility.length === 0 ? "No current calculation is saved. Complete the required inputs for the requested benefits, then calculate."
          : "Results use saved input facts. Request changes clear the current determination; awarded grants are not silently changed."}
      </p>
      {benefit.requestedBenefits.length === 0 && benefit.benefitGrants.length === 0 &&
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">No assistance has been requested. Select requested benefits in Program Details before calculating.</p>}
      {loading && <p className="text-sm text-muted-foreground">Loading active programs…</p>}
      {loadError && <div className="space-y-2"><p className="text-sm text-destructive">{loadError}</p>
        <Button variant="outline" size="sm" disabled={!canRetry} onClick={retry}>Retry program catalog</Button></div>}
      {!loading && !loadError && <>
        <p className="text-xs text-muted-foreground">
          This grid shows formula-calculated programs. Lakehaven, Arrears, Cooling, and Supplemental remain available in requested assistance and any saved determinations or grants remain authorable in Grants/Payments; the server still enforces their dependencies and caps.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
        {visibleIncomeFormulaPrograms(options).map(option => {
          const requirement = readiness?.programs.find(program => program.programId === option.programId)
          const applicable = requirement?.requested === true || requirement?.requiredByExistingGrant === true
          return <div key={`${option.programId}/${option.programYear}/${option.benefitProgramInstance?.revision ?? "unconfigured"}`} className="rounded-md border p-3 text-sm">
            <span className="font-medium">{option.name} {option.programYear}</span>
            <span className="mt-1 block text-xs text-muted-foreground">
              {applicable ? requirement?.requiredByExistingGrant ? "Existing grant requirements are pinned." : "Requested assistance." : "Not requested; no determination will be calculated."}
              {requirement?.requiredInputs.length ? <> Required: {requirement.requiredInputs.join(", ")}.</> : null}
              {applicable && requirement?.missingInputs.length ? <> Missing: {requirement.missingInputs.join(" ")}</> : null}
            </span>
          </div>
        })}
        {visibleIncomeFormulaPrograms(options).length === 0 && <p className="text-sm text-muted-foreground">No formula-calculated programs are available for this application date.</p>}
        </div>
      </>}
      {readiness?.configurationProblems.length ? <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">Configuration needs attention: {readiness.configurationProblems.join(" ")}</p> : null}
      {readiness && !readiness.readyForCalculation && readiness.programs.some(program => program.readyForCalculation && !program.dependencyOnly) &&
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">Some requested programs are ready and will be calculated; pending programs are skipped until their inputs or settings are completed.</p>}
      {canEdit && <Button disabled={!canCalculate} onClick={calculate}>
        {saving ? <IconLoader2 className="size-4 animate-spin" /> : <IconCalculator className="size-4" />}Calculate Eligibility
      </Button>}
    </CardContent>
  </Card>
}
